import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, XIcon } from '@/shared/ui/icons';
import { Input } from '@/shared/ui/inputs/Input';
import { Button } from '@/shared/ui/buttons/Button';

type Props = {
  value: string[];
  onChange: (next: string[]) => void;
};

export const FeaturesProfileLanguagesInput = ({ value, onChange }: Props) => {
  const { t } = useTranslation('profile');
  const [draft, setDraft] = useState('');

  const addLanguage = (): void => {
    const next = draft.trim();
    if (!next) return;
    const exists = value.some((item) => item.toLowerCase() === next.toLowerCase());
    if (exists) {
      setDraft('');
      return;
    }
    onChange([...value, next]);
    setDraft('');
  };

  const removeLanguage = (index: number): void => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addLanguage();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-ink-soft">{t('fields.languages')}</span>
      <div className="flex items-stretch gap-2">
        <div className="flex-1">
          <Input
            placeholder={t('languagesPlaceholder')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-12 py-0"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={addLanguage}
          className="h-12"
          leftIcon={<PlusIcon className="h-4 w-4" />}
        >
          {t('addLanguage')}
        </Button>
      </div>
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {value.map((language, index) => (
            <li
              key={`${language}-${index}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 py-1 pl-3 pr-1.5 text-sm font-medium text-brand-700"
            >
              <span className="truncate">{language}</span>
              <button
                type="button"
                onClick={() => removeLanguage(index)}
                aria-label={t('removeLanguage', { language })}
                className="inline-flex h-5 w-5 items-center justify-center rounded-full text-brand-600 transition-colors hover:bg-brand-100"
              >
                <XIcon className="h-3.5 w-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
